/**
 * Card detail (popup) server state: ordered content blocks and checklists.
 * Board-level card mutations (move, pin, complete, labels) live in entities/board.
 *
 * Every content mutation returns the whole updated card (`CardDetail`), which
 * replaces the cache and syncs the board preview (title, version, progress).
 * Text edits and structural changes send the card `version` they are based on;
 * a 409 VERSION_CONFLICT refetches the card so the user retries on fresh data.
 */
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type Card, syncCardInBoard } from '@/entities/board';
import { api, isApiError, queryKeys } from '@/shared/api';

export interface ChecklistItem {
  id: string;
  text: string;
  is_completed: boolean;
  position_key: string;
}

export interface Checklist {
  id: string;
  title: string | null;
  items: ChecklistItem[];
  completed: number;
  total: number;
}

export interface ContentBlock {
  id: string;
  block_type: 'markdown' | 'checklist';
  position_key: string;
  markdown_text: string | null;
  is_collapsed: boolean;
  checklist: Checklist | null;
}

export interface CardDetail extends Card {
  content_blocks: ContentBlock[];
}

export function useCard(cardId: string) {
  return useQuery({
    queryKey: queryKeys.cards.detail(cardId),
    queryFn: ({ signal }) => api.get<CardDetail>(`/v1/cards/${cardId}`, { signal }),
  });
}

/**
 * Warm the card cache on intent (a short hover/focus on a tile). Deduplicated by the query
 * cache and skipped while a fresh copy exists, so repeated hovering costs nothing.
 */
export function usePrefetchCard() {
  const client = useQueryClient();
  return (cardId: string) =>
    client
      .query({
        queryKey: queryKeys.cards.detail(cardId),
        queryFn: ({ signal }) => api.get<CardDetail>(`/v1/cards/${cardId}`, { signal }),
      })
      .catch(() => undefined);
}

/** Version of the card as currently cached (not as captured by a render). */
function currentVersion(client: QueryClient, cardId: string): number | undefined {
  return client.getQueryData<CardDetail>(queryKeys.cards.detail(cardId))?.version;
}

type Patch = (card: CardDetail) => CardDetail;

/** Card content mutation with optional optimistic patch + rollback. */
function useContentMutation<V, R extends Card = CardDetail>(
  cardId: string,
  request: (variables: V, version: number | undefined) => Promise<R>,
  optimistic?: (variables: V) => Patch,
) {
  const client = useQueryClient();
  const key = queryKeys.cards.detail(cardId);
  return useMutation({
    mutationFn: (variables: V) => request(variables, currentVersion(client, cardId)),
    onMutate: async (variables: V) => {
      if (!optimistic) return { previous: undefined };
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<CardDetail>(key);
      if (previous) client.setQueryData(key, optimistic(variables)(previous));
      return { previous };
    },
    onSuccess: (updated) => {
      client.setQueryData<CardDetail>(key, (current) =>
        current ? { ...current, ...updated } : current,
      );
      syncCardInBoard(client, updated);
    },
    onError: (error, _variables, context) => {
      if (context?.previous) client.setQueryData(key, context.previous);
      if (isApiError(error) && error.code === 'VERSION_CONFLICT') {
        void client.invalidateQueries({ queryKey: key });
      }
    },
  });
}

const blocksUrl = (cardId: string) => `/v1/cards/${cardId}/content-blocks`;
const itemUrl = (cardId: string, itemId: string) => `/v1/cards/${cardId}/checklist-items/${itemId}`;

export function useUpdateCardTitle(cardId: string) {
  return useContentMutation(cardId, (title: string, version) =>
    api.patch<Card>(`/v1/cards/${cardId}`, { title, expected_version: version }),
  );
}

export type Anchor = { after_block_id: string } | { before_block_id: string } | object;

export function useAddBlock(cardId: string) {
  return useContentMutation(
    cardId,
    (block: { block_type: 'markdown' | 'checklist'; markdown_text?: string } & Anchor, version) =>
      api.post<CardDetail>(blocksUrl(cardId), { ...block, expected_version: version }),
  );
}

export function useUpdateBlock(cardId: string) {
  return useContentMutation(
    cardId,
    (
      { blockId, ...data }: { blockId: string; markdown_text?: string; title?: string | null },
      version,
    ) =>
      api.patch<CardDetail>(`${blocksUrl(cardId)}/${blockId}`, {
        ...data,
        expected_version: version,
      }),
  );
}

/** Collapse/expand: view state, no version check, applied optimistically. */
export function useSetBlockCollapsed(cardId: string) {
  return useContentMutation(
    cardId,
    ({ blockId, collapsed }: { blockId: string; collapsed: boolean }) =>
      api.patch<CardDetail>(`${blocksUrl(cardId)}/${blockId}`, { is_collapsed: collapsed }),
    ({ blockId, collapsed }) =>
      (card) => ({
        ...card,
        content_blocks: card.content_blocks.map((b) =>
          b.id === blockId ? { ...b, is_collapsed: collapsed } : b,
        ),
      }),
  );
}

/** Move a block one step up/down (the non-drag reorder control), optimistically. */
export function useMoveBlock(cardId: string) {
  return useContentMutation(
    cardId,
    ({ blockId, anchor }: { blockId: string; anchor: Anchor; order: string[] }, version) =>
      api.post<CardDetail>(`${blocksUrl(cardId)}/${blockId}/move`, {
        ...anchor,
        expected_version: version,
      }),
    ({ order }) =>
      (card) => ({
        ...card,
        content_blocks: order
          .map((id) => card.content_blocks.find((b) => b.id === id))
          .filter((b): b is ContentBlock => b !== undefined),
      }),
  );
}

export function useDeleteBlock(cardId: string) {
  return useContentMutation(cardId, (blockId: string, version) =>
    api.delete<CardDetail>(`${blocksUrl(cardId)}/${blockId}`, {
      query: { expected_version: version },
    }),
  );
}

export function useAddChecklistItem(cardId: string) {
  return useContentMutation(
    cardId,
    ({ checklistId, text }: { checklistId: string; text: string }) =>
      api.post<CardDetail>(`/v1/cards/${cardId}/checklists/${checklistId}/items`, { text }),
  );
}

function patchItem(itemId: string, patch: Partial<ChecklistItem>): Patch {
  return (card) => ({
    ...card,
    content_blocks: card.content_blocks.map((block) => {
      if (!block.checklist?.items.some((item) => item.id === itemId)) return block;
      const items = block.checklist.items.map((item) =>
        item.id === itemId ? { ...item, ...patch } : item,
      );
      return {
        ...block,
        checklist: {
          ...block.checklist,
          items,
          completed: items.filter((item) => item.is_completed).length,
        },
      };
    }),
  });
}

/** Complete/reopen an item: optimistic, no version check (commutative toggle). */
export function useToggleChecklistItem(cardId: string) {
  return useContentMutation(
    cardId,
    ({ itemId, completed }: { itemId: string; completed: boolean }) =>
      api.patch<CardDetail>(itemUrl(cardId, itemId), { is_completed: completed }),
    ({ itemId, completed }) => patchItem(itemId, { is_completed: completed }),
  );
}

export function useEditChecklistItem(cardId: string) {
  return useContentMutation(cardId, ({ itemId, text }: { itemId: string; text: string }, version) =>
    api.patch<CardDetail>(itemUrl(cardId, itemId), { text, expected_version: version }),
  );
}

export function useMoveChecklistItem(cardId: string) {
  return useContentMutation(
    cardId,
    ({
      itemId,
      anchor,
    }: {
      itemId: string;
      anchor: { after_item_id: string } | { before_item_id: string };
      order: string[];
    }) => api.post<CardDetail>(`${itemUrl(cardId, itemId)}/move`, anchor),
    ({ itemId, order }) =>
      (card) => ({
        ...card,
        content_blocks: card.content_blocks.map((block) =>
          block.checklist?.items.some((item) => item.id === itemId)
            ? {
                ...block,
                checklist: {
                  ...block.checklist,
                  items: order
                    .map((id) => block.checklist?.items.find((item) => item.id === id))
                    .filter((item): item is ChecklistItem => item !== undefined),
                },
              }
            : block,
        ),
      }),
  );
}

export function useDeleteChecklistItem(cardId: string) {
  return useContentMutation(cardId, (itemId: string) =>
    api.delete<CardDetail>(itemUrl(cardId, itemId)),
  );
}
